/**
 * One-time move from the legacy single blob to per-record storage.
 *
 * Crash-safe and idempotent: the new stores are cleared and rewritten in
 * full, read back and checked, and only then is the migration marker set.
 * The legacy blob is never written or deleted here, so a crash or failure at
 * any point leaves it intact and the migration runs again on the next load.
 */
import type { GameHistoryEntry } from "../../utils/gameHistory";
import {
  createDefaultData,
  migrateData,
  type UnifiedAppData,
} from "../unifiedDataSchema";
import { backupCorrupted, LEGACY_DATA_KEY, readLegacyBlob } from "./legacy";
import {
  isPlainObject,
  MIGRATED_MARKER,
  sectionJson,
  type StateSection,
} from "./sections";
import type { HistoryKey, StorageAdapter } from "./types";

export type Migration =
  /** `legacyWritable`: the blob was read, so saving over it loses nothing. */
  | { ok: false; data: UnifiedAppData; legacyWritable: boolean }
  | {
      ok: true;
      data: UnifiedAppData;
      history: [HistoryKey, GameHistoryEntry][];
      sections: [StateSection, string][];
    };

/** The legacy blob as current data; null means it can't be read or kept safe. */
export async function readLegacy(): Promise<UnifiedAppData | null> {
  let raw: string | null;
  try {
    raw = await readLegacyBlob();
  } catch (error) {
    console.error("🚨 Could not read stored data:", error);
    return null;
  }
  if (raw === null) return createDefaultData();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) throw new Error("stored data is not an object");
    return migrateData(parsed as unknown as UnifiedAppData);
  } catch (error) {
    console.error("🚨 Stored data is unreadable:", error);
    // Defaults may replace it only once a copy is safe.
    return (await backupCorrupted(raw)) ? createDefaultData() : null;
  }
}

async function verify(
  adapter: StorageAdapter,
  history: [HistoryKey, GameHistoryEntry][],
  sections: [StateSection, string][],
): Promise<void> {
  const stored = await adapter.readHistory();
  if (stored.length !== history.length) {
    throw new Error(`stored ${stored.length} of ${history.length} games`);
  }
  stored.forEach(([key, raw], i) => {
    const [wantKey, want] = history[i];
    const got = raw as Partial<GameHistoryEntry> | null;
    if (
      key !== wantKey ||
      got?.timestamp !== want.timestamp ||
      got?.startWord !== want.startWord ||
      got?.targetWord !== want.targetWord
    ) {
      throw new Error(`game record ${wantKey} does not match`);
    }
  });
  const state = await adapter.readState();
  const missing = sections.filter(([s]) => !isPlainObject(state.get(s)));
  if (missing.length > 0) {
    throw new Error(`missing state: ${missing.map(([s]) => s).join(", ")}`);
  }
}

export async function migrateLegacy(
  adapter: StorageAdapter,
): Promise<Migration> {
  const data = await readLegacy();
  if (!data) {
    return { ok: false, data: createDefaultData(), legacyWritable: false };
  }

  // Oldest game gets key 1: a higher key is always a newer game.
  const history = [...data.gameHistory]
    .reverse()
    .map((e, i): [HistoryKey, GameHistoryEntry] => [i + 1, e]);
  const sections = sectionJson(data);
  try {
    await adapter.clear(); // leftovers of an interrupted attempt
    await adapter.writeHistory(history);
    await adapter.writeState(sections.map(([s, j]) => [s, JSON.parse(j)]));
    await verify(adapter, history, sections);
    await adapter.writeState([
      [
        MIGRATED_MARKER,
        { from: LEGACY_DATA_KEY, at: Date.now(), games: history.length },
      ],
    ]);
  } catch (error) {
    console.error(
      "🚨 Could not move stored data to the new store; it stays in the old one and the move is retried next launch:",
      error,
    );
    return { ok: false, data, legacyWritable: true };
  }
  console.log(`📦 Moved ${history.length} games to per-record storage`);
  return { ok: true, data, history, sections };
}
